import type {
  ApprovalChangeDisplay,
  ApprovalDiffEntry,
  ApprovalPermissionRow,
  ApprovalRequest
} from '@/userManagement/domain/entities/ApprovalRequest';
import type { ApprovalStatus } from '@/userManagement/domain/types/ApprovalStatus';
import type { ChangeCategory } from '@/userManagement/domain/types/ChangeCategory';
import type { ChangeType } from '@/userManagement/domain/types/ChangeType';

/** Nested user ref returned by the UM service for requests / reviewers (camelCase or PascalCase). */
export type ApprovalRequestUserRef = {
  id?: string;
  username?: string;
  fullName?: string;
  Username?: string;
  FullName?: string;
};

/** Avoid rendering raw objects in the UI (e.g. empty `{}` from API) when a string is expected. */
function coerceWireString(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function displayNameFromUserRef(u: unknown): string {
  if (u == null) return '';
  if (typeof u === 'string') return u;
  if (typeof u === 'object') {
    const o = u as ApprovalRequestUserRef;
    const name = o.username ?? o.fullName ?? o.Username ?? o.FullName;
    if (name != null && String(name).trim() !== '') return String(name);
  }
  return '';
}

function requestedOrReviewedName(v: string | unknown | null | undefined): string | null {
  if (v == null) return null;
  const s = displayNameFromUserRef(v);
  return s === '' ? null : s;
}

function parseUserRefFromWire(value: unknown): { username: string; fullName: string | null } {
  if (value == null) return { username: '', fullName: null };
  if (typeof value === 'string') return { username: value.trim(), fullName: null };
  if (typeof value === 'object') {
    const o = value as ApprovalRequestUserRef;
    const username = String(o.username ?? o.Username ?? '').trim();
    const fullNameRaw = String(o.fullName ?? o.FullName ?? '').trim();
    return { username, fullName: fullNameRaw !== '' ? fullNameRaw : null };
  }
  return { username: '', fullName: null };
}

function parseTargetUserFromWire(dto: ApprovalWireDto): { username: string; fullName: string | null } {
  const ref = dto.targetUser as ApprovalRequestUserRef | string | null | undefined;
  let username = String(dto.targetUserName ?? dto.targetUsername ?? '').trim();
  let fullName: string | null = null;

  if (ref != null && typeof ref === 'object') {
    const parsed = parseUserRefFromWire(ref);
    if (parsed.username !== '') username = parsed.username;
    fullName = parsed.fullName;
  } else if (typeof ref === 'string' && ref.trim() !== '' && username === '') {
    username = ref.trim();
  }

  if (username === '') {
    username = displayNameFromUserRef(ref).trim();
  }

  return { username, fullName };
}

function normalizeApprovalPermissionRow(row: unknown): ApprovalPermissionRow | null {
  if (row === null || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const featureIdRaw = o.featureId ?? o.FeatureId;
  const featureId =
    typeof featureIdRaw === 'number' && Number.isFinite(featureIdRaw)
      ? featureIdRaw
      : typeof featureIdRaw === 'string'
        ? parseInt(featureIdRaw, 10)
        : NaN;
  if (!Number.isFinite(featureId)) return null;
  const flag = (camel: string, pascal: string): boolean => Boolean(o[camel] ?? o[pascal] ?? false);
  return {
    featureId,
    allowRead: flag('allowRead', 'AllowRead'),
    allowWrite: flag('allowWrite', 'AllowWrite'),
    allowDelete: flag('allowDelete', 'AllowDelete'),
    allowCentralAccess: flag('allowCentralAccess', 'AllowCentralAccess'),
    allowExcelExport: flag('allowExcelExport', 'AllowExcelExport')
  };
}

/** Parses UM `currentValue` / `proposedValue`: `{}`, arrays of permission rows, or arbitrary JSON objects. */
function parseApprovalChangeDisplay(raw: unknown): ApprovalChangeDisplay | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'string') return null;
  if (Array.isArray(raw)) {
    const rows = raw.map(normalizeApprovalPermissionRow).filter((r): r is ApprovalPermissionRow => r !== null);
    return { kind: 'permissions', rows };
  }
  if (typeof raw === 'object') {
    if (Object.keys(raw as object).length === 0) {
      return { kind: 'permissions', rows: [] };
    }
    return { kind: 'other', value: raw };
  }
  return null;
}

/** Wire shape for a single BE-computed diff entry on `ApprovalRequestDetailDto.Diff`. */
export interface ApprovalDiffEntryWireDto {
  featureId?: number;
  FeatureId?: number;
  featureName?: string;
  FeatureName?: string;
  field?: string;
  Field?: string;
  oldValue?: unknown;
  OldValue?: unknown;
  newValue?: unknown;
  NewValue?: unknown;
}

/** Wire row for approval-requests list API before {@link mapApprovalDtoToEntity}. */
export interface ApprovalWireDto {
  id: number | string;
  targetUserId?: number;
  targetUsername?: string;
  targetUserName?: string;
  targetUser?: ApprovalRequestUserRef | null;
  dbName?: string;
  dbAlias?: string | null;
  DbAlias?: string | null;
  changeType: ChangeType;
  currentValue?: unknown;
  proposedValue?: unknown;
  /** TROC-124: BE-computed diff entries; present on detail endpoint, absent on list endpoint. */
  diff?: ApprovalDiffEntryWireDto[] | null;
  /** PascalCase fallback in case the API serializer emits Pascal-cased property names. */
  Diff?: ApprovalDiffEntryWireDto[] | null;
  justification: string;
  changeCategory: ChangeCategory;
  status: ApprovalStatus;
  requestedBy?: string | unknown;
  requestedAt?: string;
  utcRequestedOn?: string;
  reviewedBy?: string | unknown | null;
  reviewedAt?: string | null;
  utcReviewedOn?: string | null;
  reviewNotes: string | null;
  itAcknowledgedBy?: string | unknown | null;
  itAcknowledgedAt?: string | null;
  utcItAcknowledgedOn?: string | null;
  /**
   * TROC-126: BE populates these on `ApprovalRequestDetailDto` for UserRoleChange / legacy RoleChange
   * approvals via `PopulateApprovalRequestDetailRoleFieldsAsync`. They're absent on every other
   * change type and on the list endpoint. PascalCase fallbacks mirror the existing wire-tolerance
   * pattern used by `requestedBy` / `reviewedBy`.
   */
  currentRoleId?: number | null;
  CurrentRoleId?: number | null;
  currentRoleName?: string | null;
  CurrentRoleName?: string | null;
  newRoleId?: number | null;
  NewRoleId?: number | null;
  newRoleName?: string | null;
  NewRoleName?: string | null;
  /** TROC-304: role-template impact; detail endpoint only. */
  impactedRoleId?: number | null;
  ImpactedRoleId?: number | null;
  impactedRoleName?: string | null;
  ImpactedRoleName?: string | null;
  approvalRequestId: number | null;
  utcUpdatedOn?: string;
  isCancellable?: boolean;
  IsCancellable?: boolean;
  cancelledBy?: string | unknown | null;
  CancelledBy?: string | unknown | null;
  utcCancelledOn?: string | null;
  UtcCancelledOn?: string | null;
  cancellationReason?: string | null;
  CancellationReason?: string | null;
}

function toRequestId(v: number | string): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const p = parseInt(String(v), 10);
  return Number.isNaN(p) ? 0 : p;
}

/**
 * TROC-124: Maps a single wire diff entry to the domain shape. Skips entries with a non-numeric or
 * missing `featureId` so the FE never has to defend against malformed BE rows at render time.
 */
function normalizeApprovalDiffEntry(raw: ApprovalDiffEntryWireDto): ApprovalDiffEntry | null {
  const featureIdRaw = raw.featureId ?? raw.FeatureId;
  const featureId = typeof featureIdRaw === 'number' && Number.isFinite(featureIdRaw) ? featureIdRaw : NaN;
  if (!Number.isFinite(featureId)) return null;
  const featureNameRaw = raw.featureName ?? raw.FeatureName;
  const fieldRaw = raw.field ?? raw.Field;
  return {
    featureId,
    featureName: typeof featureNameRaw === 'string' ? featureNameRaw : '',
    field: typeof fieldRaw === 'string' ? fieldRaw : '',
    oldValue: raw.oldValue ?? raw.OldValue,
    newValue: raw.newValue ?? raw.NewValue
  };
}

function mapDiffWire(diffWire: ApprovalDiffEntryWireDto[] | null | undefined): ApprovalDiffEntry[] | undefined {
  if (diffWire == null) return undefined;
  return diffWire.map(normalizeApprovalDiffEntry).filter((d): d is ApprovalDiffEntry => d !== null);
}

const APPROVAL_STATUS_BY_LOWER: Record<string, ApprovalStatus> = {
  pendingreview: 'PendingReview',
  pendingit: 'PendingIT',
  approved: 'Approved',
  rejected: 'Rejected'
};

/** UM list/detail DTOs may emit `status` (camelCase) or `Status` (PascalCase) depending on serializer settings. */
function normalizeApprovalStatusFromWire(dto: ApprovalWireDto): ApprovalStatus {
  const o = dto as unknown as Record<string, unknown>;
  const raw = o.status ?? o.Status;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed !== '') {
      const byLower = APPROVAL_STATUS_BY_LOWER[trimmed.toLowerCase()];
      if (byLower) return byLower;
    }
  }
  return 'PendingReview';
}

export function mapApprovalDtoToEntity(dto: ApprovalWireDto): ApprovalRequest {
  const { username: targetUserName, fullName: targetUserFullName } = parseTargetUserFromWire(dto);
  const requestedByRef = parseUserRefFromWire(dto.requestedBy);
  const requestedBy = requestedByRef.username || displayNameFromUserRef(dto.requestedBy);
  const id = toRequestId(dto.id);

  const currentChange = parseApprovalChangeDisplay(dto.currentValue);
  const proposedChange = parseApprovalChangeDisplay(dto.proposedValue);

  let currentValue = '';
  if (currentChange) {
    currentValue = '';
  } else if (dto.currentValue !== undefined && dto.currentValue !== null) {
    currentValue = coerceWireString(dto.currentValue, '');
  } else {
    currentValue = coerceWireString(dto.dbName, '');
  }

  let proposedValue = '';
  if (proposedChange) {
    proposedValue = '';
  } else if (dto.proposedValue !== undefined && dto.proposedValue !== null) {
    proposedValue = coerceWireString(dto.proposedValue, '');
  }

  const diff = mapDiffWire(dto.diff ?? dto.Diff);

  return {
    id,
    targetUserId: dto.targetUserId ?? 0,
    targetUserName,
    targetUserFullName,
    changeType: dto.changeType,
    databaseName: coerceWireString(dto.dbName, ''),
    databaseAlias: (() => {
      const raw = dto.dbAlias ?? dto.DbAlias;
      if (typeof raw !== 'string') return null;
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    })(),
    currentChange: currentChange ?? null,
    proposedChange: proposedChange ?? null,
    currentValue,
    proposedValue,
    diff,
    justification: coerceWireString(dto.justification, ''),
    changeCategory: dto.changeCategory,
    status: normalizeApprovalStatusFromWire(dto),
    requestedBy,
    requestedByFullName: requestedByRef.fullName,
    requestedAt: dto.requestedAt ?? dto.utcRequestedOn ?? '',
    reviewedBy: requestedOrReviewedName(dto.reviewedBy),
    reviewedAt: dto.reviewedAt ?? dto.utcReviewedOn ?? null,
    reviewNotes: dto.reviewNotes,
    itAcknowledgedBy: requestedOrReviewedName(dto.itAcknowledgedBy),
    itAcknowledgedAt: dto.itAcknowledgedAt ?? dto.utcItAcknowledgedOn ?? null,
    currentRoleId: dto.currentRoleId ?? dto.CurrentRoleId ?? null,
    currentRoleName: dto.currentRoleName ?? dto.CurrentRoleName ?? null,
    newRoleId: dto.newRoleId ?? dto.NewRoleId ?? null,
    newRoleName: dto.newRoleName ?? dto.NewRoleName ?? null,
    impactedRoleId: dto.impactedRoleId ?? dto.ImpactedRoleId ?? null,
    impactedRoleName: (() => {
      const raw = dto.impactedRoleName ?? dto.ImpactedRoleName;
      if (typeof raw !== 'string') return null;
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    })(),
    approvalRequestId: dto.approvalRequestId ?? null,
    utcUpdatedOn: dto.utcUpdatedOn ?? dto.utcRequestedOn ?? new Date().toISOString(),
    isCancellable: Boolean(dto.isCancellable ?? dto.IsCancellable ?? false),
    cancelledBy: requestedOrReviewedName(dto.cancelledBy ?? dto.CancelledBy),
    cancelledAt: dto.utcCancelledOn ?? dto.UtcCancelledOn ?? null,
    cancellationReason: (() => {
      const raw = dto.cancellationReason ?? dto.CancellationReason;
      if (raw == null) return null;
      const s = coerceWireString(raw, '');
      return s === '' ? null : s;
    })()
  };
}
